import React, { useEffect, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogContent,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import YouTubeIcon from '@mui/icons-material/YouTube';
import InstagramIcon from '@mui/icons-material/Instagram';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import FacebookIcon from '@mui/icons-material/Facebook';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import ShareIcon from '@mui/icons-material/Share';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BusinessIcon from '@mui/icons-material/Business';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { BRAND_COLOR } from '../theme';
import { analytics, AnalyticsEvent } from '../services/analytics';
import ContactDialog from '../components/ContactDialog';

// TikTok icon component
const TikTokIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
    </svg>
);

const PageContainer = styled(Box)(() => ({
    minHeight: '100vh',
    backgroundColor: '#ffffff',
}));

const HeroSection = styled(Box)(({ theme }) => ({
    backgroundColor: BRAND_COLOR,
    color: 'white',
    padding: '120px 0 80px',
    [theme.breakpoints.down('md')]: {
        padding: '100px 0 60px',
    },
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: 'url("/images/vertical-background.svg")',
        opacity: 0.15,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
    },
}));

const ReportCard = styled(Card)(() => ({
    backgroundColor: 'white',
    borderRadius: '16px',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
    overflow: 'visible',
    border: '1px solid rgba(0, 0, 0, 0.05)',
}));

const PlatformChip = styled(Chip)(() => ({
    backgroundColor: 'rgba(26, 26, 46, 0.08)',
    color: 'rgba(26, 26, 46, 0.7)',
    fontWeight: 600,
    fontSize: '0.8rem',
    height: '28px',
    '& .MuiChip-icon': {
        color: 'rgba(26, 26, 46, 0.5)',
    },
}));

const HeroPlatformChip = styled(Chip)(() => ({
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    color: 'white',
    fontWeight: 600,
    fontSize: '0.8rem',
    height: '28px',
    '& .MuiChip-icon': {
        color: 'rgba(255, 255, 255, 0.8)',
    },
}));

const MetricBox = styled(Box)(() => ({
    textAlign: 'center',
    padding: '20px',
    borderRadius: '12px',
    backgroundColor: '#ffffff',
}));

const FeatureCard = styled(Box)(() => ({
    padding: '32px',
    borderRadius: '16px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.2s ease, background-color 0.2s ease',
    '&:hover': {
        transform: 'translateY(-4px)',
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
    },
}));

const FeatureIconWrapper = styled(Box)(() => ({
    width: 56,
    height: 56,
    borderRadius: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    color: 'white',
}));

// Fake data for the mockup
const fakeBrand = {
    name: 'Banco',
    logo: '🏦',
};

const fakePodcast = {
    name: 'El Podcast de Chile',
    image: '🎙️',
};

const fakeMetrics = {
    totalViews: 2847392,
    totalReach: 1523847,
    totalMentions: 12,
    totalContent: 8,
};

const fakePlatformStats = [
    {
        platform: 'youtube',
        name: 'YouTube',
        icon: <YouTubeIcon />,
        videos: 3,
        views: 1234567,
        likes: 45678,
    },
    {
        platform: 'spotify',
        name: 'Spotify',
        icon: <MusicNoteIcon />,
        videos: 2,
        views: 523400,
        likes: 12340,
    },
    {
        platform: 'instagram',
        name: 'Instagram',
        icon: <InstagramIcon />,
        videos: 4,
        views: 687234,
        likes: 34567,
    },
    {
        platform: 'tiktok',
        name: 'TikTok',
        icon: (
            <Box sx={{ display: 'flex' }}>
                <TikTokIcon />
            </Box>
        ),
        videos: 3,
        views: 402191,
        likes: 28934,
    },
];

const fakeMentions = [
    {
        id: 1,
        platform: 'YouTube',
        timestamp: '12:34',
        duration: '8s',
        transcript: '"...y gracias a Banco por hacer posible este episodio..."',
        link: 'https://youtube.com/watch?v=abc123&t=754',
        linkLabel: 'Link al momento',
    },
    {
        id: 2,
        platform: 'YouTube',
        timestamp: '24:15',
        duration: '15s',
        transcript: 'Logo visible durante segmento de noticias',
        link: 'https://youtube.com/watch?v=abc123&t=1455',
        linkLabel: 'Link al momento',
    },
    {
        id: 3,
        platform: 'YouTube',
        timestamp: '45:02',
        duration: '12s',
        transcript: '"Banco, el banco de todos los chilenos, nos acompaña hoy..."',
        link: 'https://youtube.com/watch?v=abc123&t=2702',
        linkLabel: 'Link al momento',
    },
    {
        id: 4,
        platform: 'Instagram',
        timestamp: '00:08',
        duration: '5s',
        transcript: 'Presencia de marca en reel promocional',
        link: 'https://instagram.com/reel/abc123',
        linkLabel: 'Link al reel',
    },
    {
        id: 5,
        platform: 'TikTok',
        timestamp: '00:12',
        duration: '6s',
        transcript: 'Logo de marca visible en video viral',
        link: 'https://tiktok.com/@podcast/video/123456',
        linkLabel: 'Link al post',
    },
];

const Reportes: React.FC = () => {
    const [contactOpen, setContactOpen] = useState(false);
    const [comingSoonOpen, setComingSoonOpen] = useState(false);

    useEffect(() => {
        analytics.track(AnalyticsEvent.VIEW_REPORTS_PAGE);
    }, []);

    const formatNumber = (num: number) => {
        if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
        if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
        return num.toString();
    };

    return (
        <PageContainer>
            {/* Hero Section */}
            <HeroSection>
                <Container
                    maxWidth="lg"
                    sx={{ position: 'relative', zIndex: 1, px: { xs: 3, md: 4 } }}
                >
                    <Box sx={{ textAlign: 'center', maxWidth: '800px', mx: 'auto' }}>
                        <Typography
                            variant="h1"
                            sx={{
                                fontSize: { xs: '2.2rem', md: '3.2rem' },
                                fontWeight: 800,
                                fontFamily: '"DM Serif Display", serif',
                                mb: 2,
                                color: 'white',
                            }}
                        >
                            Reportes para Sponsors
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: { xs: '1.1rem', md: '1.25rem' },
                                color: 'rgba(255, 255, 255, 0.85)',
                                mb: 4,
                                lineHeight: 1.6,
                            }}
                        >
                            Genera reportes profesionales para tus auspiciadores. Métricas de
                            alcance, menciones detectadas, clips de video y traducciones en todas
                            tus plataformas.
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1.5,
                                justifyContent: 'center',
                                flexWrap: 'wrap',
                            }}
                        >
                            <HeroPlatformChip icon={<YouTubeIcon />} label="YouTube" />
                            <HeroPlatformChip icon={<MusicNoteIcon />} label="Spotify" />
                            <HeroPlatformChip icon={<InstagramIcon />} label="Instagram" />
                            <HeroPlatformChip
                                icon={
                                    <Box sx={{ display: 'flex', mr: -0.5 }}>
                                        <TikTokIcon />
                                    </Box>
                                }
                                label="TikTok"
                            />
                            <HeroPlatformChip icon={<FacebookIcon />} label="Facebook" />
                        </Box>
                    </Box>
                </Container>
            </HeroSection>

            {/* Demo Report Section */}
            <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 }, px: { xs: 3, md: 4 } }}>
                <ReportCard>
                    <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                        {/* Report Header */}
                        <Box sx={{ mb: 4 }}>
                            <Typography
                                sx={{
                                    fontSize: '0.85rem',
                                    color: 'rgba(0, 0, 0, 0.5)',
                                    fontWeight: 500,
                                    mb: 1,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                }}
                            >
                                Reporte de Marca
                            </Typography>
                            <Typography
                                variant="h2"
                                sx={{
                                    fontSize: { xs: '1.5rem', md: '2rem' },
                                    fontWeight: 800,
                                    fontFamily: '"DM Serif Display", serif',
                                    color: BRAND_COLOR,
                                    mb: 0.5,
                                }}
                            >
                                {fakeBrand.name} en {fakePodcast.name}
                            </Typography>
                            <Typography sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                                Diciembre 2025 · 8 contenidos · 4 plataformas
                            </Typography>
                        </Box>

                        {/* Key Metrics */}
                        <Grid container spacing={2} sx={{ mb: 5 }}>
                            <Grid item xs={6} md={3}>
                                <MetricBox>
                                    <VisibilityIcon
                                        sx={{ fontSize: 28, color: BRAND_COLOR, mb: 1 }}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: '1.8rem',
                                            fontWeight: 800,
                                            fontFamily: '"DM Serif Display", serif',
                                            color: BRAND_COLOR,
                                        }}
                                    >
                                        {formatNumber(fakeMetrics.totalViews)}
                                    </Typography>
                                    <Typography
                                        sx={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: '0.85rem' }}
                                    >
                                        Visualizaciones
                                    </Typography>
                                </MetricBox>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <MetricBox>
                                    <TrendingUpIcon
                                        sx={{ fontSize: 28, color: BRAND_COLOR, mb: 1 }}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: '1.8rem',
                                            fontWeight: 800,
                                            fontFamily: '"DM Serif Display", serif',
                                            color: BRAND_COLOR,
                                        }}
                                    >
                                        {formatNumber(fakeMetrics.totalReach)}
                                    </Typography>
                                    <Typography
                                        sx={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: '0.85rem' }}
                                    >
                                        Alcance Total
                                    </Typography>
                                </MetricBox>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <MetricBox>
                                    <BusinessIcon
                                        sx={{ fontSize: 28, color: BRAND_COLOR, mb: 1 }}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: '1.8rem',
                                            fontWeight: 800,
                                            fontFamily: '"DM Serif Display", serif',
                                            color: BRAND_COLOR,
                                        }}
                                    >
                                        {fakeMetrics.totalMentions}
                                    </Typography>
                                    <Typography
                                        sx={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: '0.85rem' }}
                                    >
                                        Menciones
                                    </Typography>
                                </MetricBox>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <MetricBox>
                                    <PlayCircleFilledIcon
                                        sx={{ fontSize: 28, color: BRAND_COLOR, mb: 1 }}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: '1.8rem',
                                            fontWeight: 800,
                                            fontFamily: '"DM Serif Display", serif',
                                            color: BRAND_COLOR,
                                        }}
                                    >
                                        {fakeMetrics.totalContent}
                                    </Typography>
                                    <Typography
                                        sx={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: '0.85rem' }}
                                    >
                                        Contenidos
                                    </Typography>
                                </MetricBox>
                            </Grid>
                        </Grid>

                        {/* Platform Breakdown */}
                        <Typography
                            sx={{
                                fontSize: '1.2rem',
                                fontWeight: 700,
                                fontFamily: '"DM Serif Display", serif',
                                mb: 3,
                                color: BRAND_COLOR,
                            }}
                        >
                            Desglose por Plataforma
                        </Typography>
                        <Grid container spacing={2} sx={{ mb: 5 }}>
                            {fakePlatformStats.map(platform => (
                                <Grid item xs={6} md={3} key={platform.platform}>
                                    <Box
                                        sx={{
                                            p: 2.5,
                                            borderRadius: '12px',
                                            border: '1px solid rgba(0, 0, 0, 0.08)',
                                            height: '100%',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                mb: 2,
                                            }}
                                        >
                                            <PlatformChip
                                                icon={platform.icon}
                                                label={platform.name}
                                                size="small"
                                            />
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontSize: '1.4rem',
                                                fontWeight: 700,
                                                fontFamily: '"DM Serif Display", serif',
                                            }}
                                        >
                                            {formatNumber(platform.views)}
                                        </Typography>
                                        <Typography
                                            sx={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: '0.8rem' }}
                                        >
                                            {platform.videos} videos ·{' '}
                                            {formatNumber(platform.likes)} likes
                                        </Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>

                        {/* Brand Mentions */}
                        <Typography
                            sx={{
                                fontSize: '1.2rem',
                                fontWeight: 700,
                                fontFamily: '"DM Serif Display", serif',
                                mb: 3,
                                color: BRAND_COLOR,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <ContentCutIcon /> Menciones Detectadas
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                            {fakeMentions.map(mention => (
                                <Box
                                    key={mention.id}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: { xs: 'column', md: 'row' },
                                        gap: { xs: 2, md: 3 },
                                        p: 2,
                                        borderRadius: '12px',
                                        border: '1px solid rgba(0, 0, 0, 0.08)',
                                        alignItems: { md: 'center' },
                                    }}
                                >
                                    {/* Content Info */}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 2,
                                                mb: 1,
                                                flexWrap: 'wrap',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Chip
                                                icon={
                                                    mention.platform === 'YouTube' ? (
                                                        <YouTubeIcon sx={{ fontSize: 16 }} />
                                                    ) : mention.platform === 'Instagram' ? (
                                                        <InstagramIcon sx={{ fontSize: 16 }} />
                                                    ) : (
                                                        <Box sx={{ display: 'flex', ml: 0.5 }}>
                                                            <TikTokIcon />
                                                        </Box>
                                                    )
                                                }
                                                label={mention.platform}
                                                size="small"
                                                sx={{
                                                    backgroundColor: 'rgba(26, 26, 46, 0.08)',
                                                    color: 'rgba(26, 26, 46, 0.7)',
                                                    fontWeight: 600,
                                                    fontSize: '0.75rem',
                                                    height: '24px',
                                                    '& .MuiChip-icon': {
                                                        color: 'rgba(26, 26, 46, 0.7)',
                                                    },
                                                }}
                                            />
                                            <Typography
                                                sx={{
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.9rem',
                                                    color: BRAND_COLOR,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {mention.timestamp}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: '0.85rem',
                                                    color: 'rgba(0, 0, 0, 0.5)',
                                                }}
                                            >
                                                {mention.duration}
                                            </Typography>
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontSize: '0.95rem',
                                                mb: 1,
                                                color: 'rgba(0, 0, 0, 0.8)',
                                            }}
                                        >
                                            {mention.transcript}
                                        </Typography>
                                        <Button
                                            href={mention.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            size="small"
                                            sx={{
                                                color: BRAND_COLOR,
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                fontSize: '0.85rem',
                                                p: 0,
                                                minWidth: 'auto',
                                                '&:hover': {
                                                    backgroundColor: 'transparent',
                                                    textDecoration: 'underline',
                                                },
                                            }}
                                        >
                                            {mention.linkLabel} →
                                        </Button>
                                    </Box>

                                    {/* Clip Thumbnail */}
                                    <Box
                                        sx={{
                                            width: { xs: '100%', md: 160 },
                                            height: { xs: 120, md: 90 },
                                            borderRadius: '8px',
                                            backgroundColor: BRAND_COLOR,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            cursor: 'pointer',
                                            position: 'relative',
                                            overflow: 'hidden',
                                            '&:hover': {
                                                '& .play-icon': {
                                                    transform: 'scale(1.1)',
                                                },
                                            },
                                        }}
                                    >
                                        <PlayCircleFilledIcon
                                            className="play-icon"
                                            sx={{
                                                fontSize: 36,
                                                color: 'white',
                                                opacity: 0.9,
                                                transition: 'transform 0.2s ease',
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                bottom: 6,
                                                left: 6,
                                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                color: 'white',
                                                px: 0.8,
                                                py: 0.2,
                                                borderRadius: '4px',
                                                fontSize: '0.7rem',
                                                fontWeight: 500,
                                            }}
                                        >
                                            {mention.timestamp}
                                        </Box>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </CardContent>
                </ReportCard>
            </Container>

            {/* Features Section */}
            <Box sx={{ backgroundColor: BRAND_COLOR, py: { xs: 8, md: 10 } }}>
                <Container maxWidth="lg" sx={{ px: { xs: 3, md: 4 } }}>
                    <Typography
                        sx={{
                            fontSize: { xs: '1.8rem', md: '2.2rem' },
                            fontWeight: 800,
                            fontFamily: '"DM Serif Display", serif',
                            textAlign: 'center',
                            mb: 2,
                            color: 'white',
                        }}
                    >
                        ¿Qué incluye cada reporte?
                    </Typography>
                    <Typography
                        sx={{
                            textAlign: 'center',
                            color: 'rgba(255, 255, 255, 0.7)',
                            mb: 6,
                            maxWidth: '600px',
                            mx: 'auto',
                        }}
                    >
                        Todo lo que necesitas para demostrar el valor de tu contenido a tus
                        auspiciadores.
                    </Typography>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <FeatureCard>
                                <FeatureIconWrapper>
                                    <PlayCircleFilledIcon sx={{ fontSize: 28 }} />
                                </FeatureIconWrapper>
                                <Typography
                                    sx={{
                                        fontSize: '1.2rem',
                                        fontWeight: 700,
                                        fontFamily: '"DM Serif Display", serif',
                                        mb: 1.5,
                                        color: 'white',
                                    }}
                                >
                                    Todas las Plataformas
                                </Typography>
                                <Typography
                                    sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.7 }}
                                >
                                    Métricas unificadas de YouTube, Spotify, Instagram, TikTok y
                                    Facebook. Un solo reporte para todas las redes.
                                </Typography>
                            </FeatureCard>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FeatureCard>
                                <FeatureIconWrapper>
                                    <ContentCutIcon sx={{ fontSize: 28 }} />
                                </FeatureIconWrapper>
                                <Typography
                                    sx={{
                                        fontSize: '1.2rem',
                                        fontWeight: 700,
                                        fontFamily: '"DM Serif Display", serif',
                                        mb: 1.5,
                                        color: 'white',
                                    }}
                                >
                                    Clips de Menciones
                                </Typography>
                                <Typography
                                    sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.7 }}
                                >
                                    Detectamos y recortamos automáticamente cada mención de marca
                                    con timestamps precisos y frames capturados.
                                </Typography>
                            </FeatureCard>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FeatureCard>
                                <FeatureIconWrapper>
                                    <ShareIcon sx={{ fontSize: 28 }} />
                                </FeatureIconWrapper>
                                <Typography
                                    sx={{
                                        fontSize: '1.2rem',
                                        fontWeight: 700,
                                        fontFamily: '"DM Serif Display", serif',
                                        mb: 1.5,
                                        color: 'white',
                                    }}
                                >
                                    Reportes Compartibles
                                </Typography>
                                <Typography
                                    sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.7 }}
                                >
                                    Genera links públicos para compartir con sponsors o descarga
                                    reportes en PDF listos para presentar.
                                </Typography>
                            </FeatureCard>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* CTA Section - Two Cards */}
            <Box sx={{ backgroundColor: BRAND_COLOR, py: { xs: 8, md: 10 } }}>
                <Container maxWidth="lg" sx={{ px: { xs: 3, md: 4 } }}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                            gap: 3,
                        }}
                    >
                        {/* Podcast Card */}
                        <Box
                            sx={{
                                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                borderRadius: '20px',
                                p: { xs: 3, md: 4 },
                                border: '1px solid rgba(255, 255, 255, 0.15)',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Box sx={{ flex: 1 }}>
                                <Typography
                                    sx={{
                                        fontSize: { xs: '1.4rem', md: '1.5rem' },
                                        fontWeight: 700,
                                        fontFamily: '"DM Serif Display", serif',
                                        color: 'white',
                                        mb: 2,
                                    }}
                                >
                                    ¿Tienes un podcast o productora?
                                </Typography>
                                <Typography
                                    sx={{
                                        color: 'rgba(255, 255, 255, 0.8)',
                                        fontSize: '0.95rem',
                                        fontFamily: '"DM Sans", sans-serif',
                                        lineHeight: 1.7,
                                        mb: 3,
                                    }}
                                >
                                    Crea el perfil público de tu programa con métricas verificadas,
                                    posición en rankings y un link profesional para compartir con
                                    sponsors, marcas y agencias.
                                </Typography>
                            </Box>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    analytics.track(AnalyticsEvent.CLICK_CREATE_PROFILE, {
                                        location: 'reportes_page',
                                    });
                                    setComingSoonOpen(true);
                                }}
                                endIcon={<ArrowForwardIcon />}
                                sx={{
                                    color: 'white',
                                    borderColor: 'white',
                                    borderWidth: 2,
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    px: 3,
                                    py: 1.2,
                                    borderRadius: '12px',
                                    fontSize: '0.95rem',
                                    '&:hover': {
                                        borderColor: 'white',
                                        borderWidth: 2,
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    },
                                }}
                            >
                                Crear perfil gratuito
                            </Button>
                        </Box>

                        {/* Brands & Agencies Card */}
                        <Box
                            sx={{
                                backgroundColor: 'white',
                                borderRadius: '20px',
                                p: { xs: 3, md: 4 },
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Box sx={{ flex: 1 }}>
                                <Typography
                                    sx={{
                                        fontSize: { xs: '1.4rem', md: '1.5rem' },
                                        fontWeight: 700,
                                        fontFamily: '"DM Serif Display", serif',
                                        color: BRAND_COLOR,
                                        mb: 2,
                                    }}
                                >
                                    ¿Listo para impresionar a tus sponsors?
                                </Typography>
                                <Typography
                                    sx={{
                                        color: 'rgba(26, 26, 46, 0.7)',
                                        fontSize: '0.95rem',
                                        fontFamily: '"DM Sans", sans-serif',
                                        lineHeight: 1.7,
                                        mb: 3,
                                    }}
                                >
                                    Genera reportes profesionales que demuestran el valor de tu
                                    contenido a marcas y agencias.
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    analytics.track(AnalyticsEvent.CLICK_REQUEST_REPORTS, {
                                        location: 'reportes_page',
                                    });
                                    setContactOpen(true);
                                }}
                                endIcon={<ArrowForwardIcon />}
                                sx={{
                                    backgroundColor: BRAND_COLOR,
                                    color: 'white',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    px: 3,
                                    py: 1.2,
                                    borderRadius: '12px',
                                    fontSize: '0.95rem',
                                    '&:hover': {
                                        backgroundColor: BRAND_COLOR,
                                    },
                                }}
                            >
                                Solicitar acceso a reportes
                            </Button>
                        </Box>
                    </Box>
                </Container>
            </Box>

            <ContactDialog
                open={contactOpen}
                onClose={() => setContactOpen(false)}
                title="Solicitar acceso a reportes"
                subtitle="Completa el formulario y te contactaremos para entender tu podcast
y mostrarte cómo se generan los reportes para sponsors y agencias."
            />

            {/* Coming Soon Dialog */}
            <Dialog
                open={comingSoonOpen}
                onClose={() => setComingSoonOpen(false)}
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        maxWidth: '400px',
                        textAlign: 'center',
                        p: 2,
                        backgroundColor: 'white',
                    },
                }}
            >
                <DialogContent>
                    <Typography
                        sx={{
                            fontSize: '2.5rem',
                            mb: 2,
                        }}
                    >
                        🚀
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '1.5rem',
                            fontWeight: 700,
                            fontFamily: '"DM Serif Display", serif',
                            color: BRAND_COLOR,
                            mb: 1.5,
                        }}
                    >
                        Próximamente
                    </Typography>
                    <Typography
                        sx={{
                            color: 'rgba(26, 26, 46, 0.7)',
                            fontSize: '0.95rem',
                            fontFamily: '"DM Sans", sans-serif',
                            lineHeight: 1.6,
                            mb: 2,
                        }}
                    >
                        Estamos trabajando en esta funcionalidad. ¡Pronto podrás crear tu perfil
                        público!
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => setComingSoonOpen(false)}
                        sx={{
                            backgroundColor: BRAND_COLOR,
                            color: 'white',
                            fontWeight: 600,
                            textTransform: 'none',
                            px: 4,
                            py: 1,
                            borderRadius: '10px',
                            '&:hover': {
                                backgroundColor: BRAND_COLOR,
                            },
                        }}
                    >
                        Entendido
                    </Button>
                </DialogContent>
            </Dialog>
        </PageContainer>
    );
};

export default Reportes;
